import { m } from 'framer-motion';
import { useState, useCallback, useEffect, useRef } from 'react';
// @mui
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

// hooks

import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';

// components

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { varHover } from 'src/components/animate';

//

import NotificationItem, { OnboardingItem, WorkspaceAccessItem } from './notification-item';
import { Invitation, useInvitationsQuery, WorkspaceList } from 'src/context/api/workspaces';
import { Avatar, Button, LinearProgress, ListItemAvatar, ListItemButton, ListItemText, Paper, Skeleton, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import LoadingButton from '@mui/lab/LoadingButton';
import { useGetBrandsQuery } from 'src/context/api/competitorsMonitoring/api';
import { setFilterAdvertizerPLA, setFilterBrandPLA, setFilterCountry, setFilterDomainPLA, setFilterKeyword, setNotificationBrandInfringement, setNotificationBrandInfringementTXT, setFilterDomainTXT, setFilterBrandTXT, setNotificationBrandInNonBrandCampaignsFilterEnabled } from 'src/context/reducers/filter-settings/filterSlice';
import { setAdType } from 'src/context/reducers/visibility/adsSlice';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useRouter } from 'src/routes/hooks';
import { Job, useExportJobsQuery, useExportMutation } from 'src/context/api/data/api';
import useNotifications from './use-notifications';
import { useCurrentUserQuery } from 'src/context/api/auth/api';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

/**
 * Notifications Popover
 *
 * This component is used to display the notifications popover.
 * It is used to display the notifications, export jobs, and other information.
 *
 * @returns {JSX.Element} The Notifications Popover component
 */

export default function NotificationsPopover() {
  const drawer = useBoolean();
  const { t } = useTranslation();
  const smUp = useResponsive('up', 'sm');
  const filterStore = useSelector((state: any) => state.filters);
  const [currentTab, setCurrentTab] = useState('all');
  const dispatch = useDispatch();
  const workspace: any = useCurrentWorkspace();
  const theme: any = useTheme();
  const [exportSelected, setExportSelected] = useState<'new' | 'previous'>('new');
  const user = useCurrentUserQuery();
  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);
  const [retryExport] = useExportMutation();
  const invitations = useInvitationsQuery(null);
  const { data: exportJobs, isLoading: isLoadingExportJobs, isFetching: isFetchingExportJobs, refetch: refetchExportJobs } = useExportJobsQuery(undefined, { pollingInterval: 5 * 1000 });
  const exportJobStatusesRef = useRef<Record<string | number, string>>({});
  const exportJobsInitializedRef = useRef(false);
  const downloadExport = useCallback((exportId: string | number) => {
    const link = document.createElement('a');
    link.href =
      process.env.CROSS_API_URL +
      `/traffic/export/${exportId}/download`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);
  useEffect(() => {
    if (!exportJobs) {
      return;
    }
    const jobs = exportJobs.jobs ?? [];
    if (!exportJobsInitializedRef.current) {
      jobs.forEach((job: Job) => {
        exportJobStatusesRef.current[job.id] = job.status;
      });
      exportJobsInitializedRef.current = true;
      return;
    }
    let downloadStarted = false;
    jobs.forEach((job: Job) => {
      const previousStatus =
        exportJobStatusesRef.current[job.id];
      if (
        job.status === 'completed' &&
        previousStatus !== 'completed' &&
        previousStatus !== 'downloaded'
      ) {
        downloadExport(job.id);
        const exportLabel =
          String(job.name ?? 'Traffic export')
            .replace(/^\[([^\]]+)\]\s*/, '$1 ')
            .replace(
              /Traffic details export$/i,
              'Traffic export'
            );
        enqueueSnackbar(
          `${exportLabel} is complete & downloaded.`,
          {
            autoHideDuration: 10000,
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
            content: (snackbarId) => (
              <Paper
                elevation={0}
                sx={{
                  width: 588,
                  maxWidth: 'calc(100vw - 32px)',
                  minHeight: 52,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: '6px 12px 6px 8px',
                  bgcolor: '#FFFFFF',
                  borderRadius: '8px',
                  boxShadow:
                    '0px 8px 16px rgba(145, 158, 171, 0.20), 0px 0px 2px rgba(145, 158, 171, 0.24)',
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    bgcolor: '#EAF8F3',
                  }}
                >
                  <Iconify
                    icon="solar:check-circle-linear"
                    width={20}
                    sx={{
                      color: '#007867',
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    flex: 1,
                    color: '#333C47',
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '22px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {exportLabel} is complete & downloaded.
                </Typography>
                <IconButton
                  size="small"
                  onClick={() =>
                    closeSnackbar(snackbarId)
                  }
                  sx={{
                    width: 24,
                    height: 24,
                    p: 0,
                    flexShrink: 0,
                    color: '#919EAB',
                  }}
                >
                  <Iconify
                    icon="mingcute:close-line"
                    width={16}
                  />
                </IconButton>
              </Paper>
            ),
          }
        );
        downloadStarted = true;
      }
      exportJobStatusesRef.current[job.id] = job.status;
    });
    if (downloadStarted) {
      window.setTimeout(() => {
        refetchExportJobs();
      }, 1000);
    }
  }, [downloadExport, exportJobs?.jobs, refetchExportJobs]);
  const { notifications, isLoadingNotifications, isFetchingNotifications } = useNotifications();
  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>
      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );
  const role = user.data?.role?.toLowerCase();
  const unreadNotificationsCount =
    role === 'customer' || role === 'consultant'
      ? 0
      : (notifications?.filter((n) => n.status === 'unread').length ?? 0);
  const workspaceAccessNotifications = notifications?.filter(
    (n) => n.type === 'workspace_access' && n.status !== 'dismissed'
  ) ?? [];
  const TABS = [
    {
      value: 'all',
      label: t('dashboard.global.all'),
      count:
        (invitations.data?.length ?? 0) +
        workspaceAccessNotifications.filter((n) => n.status === 'unread').length +
        (filterStore?.notificationBrandInNonBrandCampaigns ? 1 : 0) +
        (workspace?.showPlaAlert &&
          (filterStore?.notificationBrandInfringementNotification?.ads?.length ?? 0) > 0
          ? 1
          : 0) +
        (workspace?.showTxtAlert &&
          (filterStore?.notificationBrandInfringementNotificationTXT?.ads?.length ?? 0) > 0
          ? 1
          : 0)
    },
  ];
  if (user.data?.role.toLowerCase() === 'customer' || user.data?.role.toLowerCase() === 'consultant') {
    TABS.splice(2, 1);
  }
  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab}>
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            tab.value === 'all' ? (
              <Label
                variant="filled"
                color="default"
              >
                {tab.count}
              </Label>
            ) : undefined
          }
          sx={{
            '&:not(:last-of-type)': {
              mr: 3,
            },
          }}
        />
      ))}
    </Tabs>
  );
  const { data: brands } = useGetBrandsQuery({ bqTable: workspace?.bqTable }, { skip: !workspace?.bqTable });
  const renderAvatar = (
    <ListItemAvatar>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: 'background.neutral',
        }}
      >
        <Box sx={{
          p: 1,
          backgroundColor: theme.palette.mode === 'dark' ? '#28323D' : ' #F4F6F8',
          color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
          borderRadius: '10px',
          padding: '10px',
          width: 44
        }}>
          <Box component="img" src={`https://www.google.com/s2/favicons?domain=${workspace?.url}&sz=64`} sx={{ width: 24, height: 24, borderRadius: '5px' }} />
        </Box>
      </Stack>
    </ListItemAvatar>
  );
  const renderList = (
    <Scrollbar>
      <List disablePadding>
        {invitations?.data?.map((invitation: Invitation) => (
          <NotificationItem key={invitation.id} notification={invitation} />
        ))}
        {workspaceAccessNotifications.map((notification) => (
          <WorkspaceAccessItem key={notification.id} notification={notification} />
        ))}
        {workspace?.showPlaAlert &&
          <>
            {filterStore?.notificationBrandInfringementNotification?.ads &&
              filterStore.notificationBrandInfringementNotification.ads.length !== 0 && (
                <>
                  <ListItemButton
                    disableRipple
                    sx={{
                      p: 2.5,
                      flexGrow: 0,
                      alignItems: 'flex-start',
                      backgroundColor: 'rgba(255, 86, 48, 0.08)',
                      borderBottom: (theme: any) => `dashed 1px ${theme.palette.divider}`,
                    }}
                  >
                    {renderAvatar}
                    <Box
                      sx={{
                        top: 26,
                        width: 8,
                        height: 8,
                        right: 20,
                        borderRadius: '50%',
                        bgcolor: '#ec6240',
                        position: 'absolute',
                      }}
                    />
                    <Stack sx={{ flexGrow: 1, color: theme.palette.mode === 'dark' ? '#e1a381' : '#B71D18', fontWeight: 800 }}>
                      Brand Infringement (PLA)
                      <ListItemText
                        disableTypography
                        secondary={
                          <Stack
                            direction="row"
                            alignItems="center"
                            sx={{ typography: 'caption', color: theme.palette.mode === 'dark' ? '#e1a381' : '#B71D18' }}
                            divider={
                              <Box
                                sx={{
                                  width: 2,
                                  height: 2,
                                  bgcolor: 'currentColor',
                                  mx: 0.5,
                                  borderRadius: '50%',
                                }}
                              />
                            }
                          >
                            Some competitors are using your Brand in their Product Listing Ads Content.
                          </Stack>
                        }
                      />
                      <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
                        <LoadingButton onClick={() => {
                          dispatch(setFilterBrandPLA(['__BRAND_INFRINGEMENT__'] as any));
                          dispatch(setAdType('PLA'));
                          dispatch(setFilterKeyword([]));
                          dispatch(setFilterCountry([]));
                          dispatch(setFilterDomainPLA([]));
                          dispatch(setFilterAdvertizerPLA([]));
                          dispatch(setNotificationBrandInfringement(false));
                          router.push(`/brand/visibility/ads?workspace=${workspace?.id}`);
                          drawer.onFalse();
                        }} size="small" variant="contained" sx={{ backgroundColor: '#ec6240', color: 'white' }}>
                          Show
                        </LoadingButton>
                      </Stack>
                    </Stack>
                  </ListItemButton>
                </>
              )}
          </>
        }
        {workspace?.showTxtAlert &&
          <>
            {filterStore?.notificationBrandInfringementNotificationTXT?.ads &&
              filterStore.notificationBrandInfringementNotificationTXT.ads.length !== 0 && (
                <>
                  <ListItemButton
                    disableRipple
                    sx={{
                      p: 2.5,
                      flexGrow: 0,
                      alignItems: 'flex-start',
                      backgroundColor: 'rgba(255, 86, 48, 0.08)',
                      borderBottom: (theme: any) => `dashed 1px ${theme.palette.divider}`,
                    }}
                  >
                    {renderAvatar}
                    <Box
                      sx={{
                        top: 26,
                        width: 8,
                        height: 8,
                        right: 20,
                        borderRadius: '50%',
                        bgcolor: '#ec6240',
                        position: 'absolute',
                      }}
                    />
                    <Stack sx={{ flexGrow: 1, color: '#e1a381', fontWeight: 800 }}>
                      Brand Infringement (Text Ads)
                      <ListItemText
                        disableTypography
                        secondary={
                          <Stack
                            direction="row"
                            alignItems="center"
                            sx={{ typography: 'caption', color: '#e1a381' }}
                            divider={
                              <Box
                                sx={{
                                  width: 2,
                                  height: 2,
                                  bgcolor: 'currentColor',
                                  mx: 0.5,
                                  borderRadius: '50%',
                                }}
                              />
                            }
                          >
                            Some competitors are using your Brand in their Text Ads Content.
                          </Stack>
                        }
                      />
                      <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
                        <LoadingButton onClick={() => {
                          dispatch(setFilterBrandPLA(['__BRAND_INFRINGEMENT__'] as any));
                          dispatch(setAdType('TEXT'));
                          dispatch(setFilterKeyword([]));
                          dispatch(setFilterCountry([]));
                          dispatch(setFilterDomainPLA([]));
                          dispatch(setFilterDomainTXT([]));
                          dispatch(setNotificationBrandInfringementTXT(false));
                          router.push(`/brand/visibility/ads?workspace=${workspace?.id}`);
                          drawer.onFalse();
                        }} size="small" variant="contained" sx={{ backgroundColor: '#ec6240', color: 'white' }}>
                          Show
                        </LoadingButton>
                      </Stack>
                    </Stack>
                  </ListItemButton>
                </>
              )}
          </>
        }
        {filterStore?.notificationBrandInNonBrandCampaigns &&
          <>
            <ListItemButton
              disableRipple
              sx={{
                p: 2.5,
                flexGrow: 0,
                alignItems: 'flex-start',
                backgroundColor: 'rgba(255, 86, 48, 0.08)',
                borderBottom: (theme: any) => `dashed 1px ${theme.palette.divider}`,
              }}
            >
              {renderAvatar}
              <Box
                sx={{
                  top: 26,
                  width: 8,
                  height: 8,
                  right: 20,
                  borderRadius: '50%',
                  bgcolor: '#ec6240',
                  position: 'absolute',
                }}
              />
              <Stack sx={{ flexGrow: 1, color: '#e1a381', fontWeight: 800 }}>
                Brand Campaigns Isolation
                <ListItemText
                  disableTypography
                  secondary={
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{ typography: 'caption', color: '#e1a381' }}
                      divider={
                        <Box
                          sx={{
                            width: 2,
                            height: 2,
                            bgcolor: 'currentColor',
                            mx: 0.5,
                            borderRadius: '50%',
                          }}
                        />
                      }
                    >
                      Non-brand campaigns are targeting branded search terms.
                    </Stack>
                  }
                />
                <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
                  <LoadingButton onClick={() => {
                    dispatch(setNotificationBrandInNonBrandCampaignsFilterEnabled(true));
                          router.push(`/brand/campaign-leaks?workspace=${workspace?.id}`);
                  }} size="small" variant="contained" sx={{ backgroundColor: '#ec6240', color: 'white' }}>
                    Show
                  </LoadingButton>
                </Stack>
              </Stack>
            </ListItemButton>
          </>
        }
      </List>
    </Scrollbar>
  );
  const handleDrawerNotification = () => {
    drawer.onTrue();
  };
  const router = useRouter();
  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={handleDrawerNotification}
      >
        <Badge
          badgeContent={
            (invitations.data?.length ?? 0) +
            workspaceAccessNotifications.filter((n) => n.status === 'unread').length +
            ((filterStore?.notificationBrandInNonBrandCampaigns) ? 1 : 0) +
            (workspace?.showPlaAlert && (filterStore?.notificationBrandInfringementNotification?.ads?.length ?? 0) > 0 ? 1 : 0) +
            (workspace?.showTxtAlert && (filterStore?.notificationBrandInfringementNotificationTXT?.ads?.length ?? 0) > 0 ? 1 : 0)}
          color="error"
        >
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>
      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420 },
        }}
      >
        {renderHead}
        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: 2.5, pr: 1 }}
        >
          {renderTabs}
        </Stack>
        <Divider />
        {currentTab === 'all' && renderList}
      </Drawer>
    </>
  );
}